/**
 * Single source of truth for "what area is the kitchen?".
 *
 * The wall-clipping path (getKitchenBoundaryPolygons in kitchenPerimeter.js)
 * and the island/walkway path (kitchenSectionPolygonRaw in walkwayContext.js)
 * used to decide this independently, with subtly different rules (label
 * case-sensitivity, section-count gate, single-vs-list return). They now both
 * delegate here.
 *
 * An explicit override (room.kitchenArea) wins. Otherwise the kitchen is
 * derived exactly as before: the kitchen Voronoi cell(s) for multi-section
 * rooms, else the whole floor polygon.
 *
 * Pure: data in, data out. No I/O.
 */
const { getSectionPolygons } = require('../geometry/sectionPolygons');

/**
 * @typedef {{ x: number, z: number }} Point2D
 * @typedef {Point2D[]} Polygon  // CCW, X-Z plane
 * @typedef {Object} KitchenRegion
 * @property {Polygon[]} polygons          List (possibly empty). Single-polygon
 *   callers take polygons[0].
 * @property {'override'|'voronoi'|'floor'} source
 * @property {string[]} contributingRoomIds Rooms whose walls/floors feed the
 *   kitchen workspace (just this room until the cross-room phase).
 */

/**
 * @param {Object} room - room with `sections`, `floors`, optional `kitchenArea`
 * @param {Object} [opts]
 * @param {Object[]} [opts.allRooms] - reserved for the cross-room phase; ignored here
 * @returns {KitchenRegion}
 */
function resolveKitchenRegion(room, opts = {}) {
  // 1. Explicit override wins.
  const override = room?.kitchenArea?.polygon;
  if (Array.isArray(override) && override.length >= 3) {
    return {
      polygons: [override.map((p) => ({ x: p.x, z: p.z }))],
      source: 'override',
      contributingRoomIds:
        room.kitchenArea.contributingRoomIds ?? (room?.id ? [room.id] : []),
    };
  }

  const contributingRoomIds = room?.id ? [room.id] : [];
  const sections = (room?.sections ?? []).filter((s) => s?.center && s?.label);

  // 2. Multi-section: kitchen Voronoi cell(s). Case-insensitive label match,
  //    collecting every kitchen-labeled cell (unifies the old list-vs-first
  //    and case-sensitive-vs-insensitive split).
  if (sections.length >= 2) {
    // Pass the already-filtered sections so the gate above and the Voronoi
    // input use the identical section set (getSectionPolygons re-applies the
    // same center && label filter, so this is behavior-identical today).
    const byLabel = getSectionPolygons({ ...room, sections });
    const polygons = [];
    for (const [label, polys] of byLabel) {
      if ((label || '').toLowerCase() === 'kitchen') polygons.push(...polys);
    }
    return { polygons, source: 'voronoi', contributingRoomIds };
  }

  // 3. Single-section / no sections: the whole floor polygon.
  const corners = room?.floors?.[0]?.polygonCorners;
  if (corners && corners.length >= 3) {
    return {
      polygons: [corners.map((p) => ({ x: p.x, z: p.z }))],
      source: 'floor',
      contributingRoomIds,
    };
  }

  return { polygons: [], source: 'floor', contributingRoomIds };
}

module.exports = { resolveKitchenRegion };
